import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    Bold, Italic, Underline as UnderlineIcon, Strikethrough,
    Highlighter, Heading1, Heading2, Heading3, List, ListOrdered,
    CheckSquare, Quote, Image as ImageIcon, Palette, Superscript as SuperscriptIcon,
    Subscript as SubscriptIcon, Undo2, Redo2
} from 'lucide-react';
import { TEXT_COLORS, HIGHLIGHT_COLORS } from './EditorConfig';

const ColorSwatches = ({ onSelect, colors = TEXT_COLORS }) => (
    <div className="tiptap-color-picker">
        {colors.map(c => (
            <button
                key={c.label}
                className="btn btn-ghost btn-sm tiptap-color-picker__swatch"
                title={c.label}
                style={{ background: c.value || 'var(--text-primary)', border: c.value ? 'none' : 'var(--border-width-default) dashed var(--border-default)' }}
                onMouseDown={(e) => { e.preventDefault(); onSelect(c.value); }}
            />
        ))}
    </div>
);

export default function EditorToolbar({ editor }) {
    const [toolbarColorOpen, setToolbarColorOpen] = useState(false);
    const [toolbarHlOpen, setToolbarHlOpen] = useState(false);
    
    const toolbarColorRef = useRef(null);
    const toolbarHlRef = useRef(null);

    const applyColor = useCallback((color) => {
        if (!editor) return;
        if (color === null) {
            editor.chain().focus().unsetColor().run();
        } else {
            editor.chain().focus().setColor(color).run();
        }
        setToolbarColorOpen(false);
    }, [editor]);

    const applyHighlight = useCallback((color) => {
        if (!editor) return;
        if (color === null) {
            editor.chain().focus().unsetHighlight().run();
        } else {
            editor.chain().focus().setHighlight({ color }).run();
        }
        setToolbarHlOpen(false);
    }, [editor]);

    // Close pickers on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (toolbarColorRef.current && !toolbarColorRef.current.contains(e.target)) {
                setToolbarColorOpen(false);
            }
            if (toolbarHlRef.current && !toolbarHlRef.current.contains(e.target)) {
                setToolbarHlOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    if (!editor) return null;

    return (
        <div className="tiptap-toolbar">
            <div className="tiptap-toolbar__group">
                <button
                    className={`tiptap-toolbar__btn ${editor.isActive('bold') ? 'tiptap-toolbar__btn--active' : ''}`}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    title="Bold"
                >
                    <Bold size={15} />
                </button>
                <button
                    className={`tiptap-toolbar__btn ${editor.isActive('italic') ? 'tiptap-toolbar__btn--active' : ''}`}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    title="Italic"
                >
                    <Italic size={15} />
                </button>
                <button
                    className={`tiptap-toolbar__btn ${editor.isActive('underline') ? 'tiptap-toolbar__btn--active' : ''}`}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    title="Underline"
                >
                    <UnderlineIcon size={15} />
                </button>
                <button
                    className={`tiptap-toolbar__btn ${editor.isActive('strike') ? 'tiptap-toolbar__btn--active' : ''}`}
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    title="Strikethrough"
                >
                    <Strikethrough size={15} />
                </button>

                <div ref={toolbarHlRef} style={{ position: 'relative' }}>
                    <button
                        className={`tiptap-toolbar__btn tiptap-toolbar__btn--hl ${editor.isActive('highlight') ? 'tiptap-toolbar__btn--active' : ''}`}
                        onClick={() => setToolbarHlOpen(o => !o)}
                        title="Highlight Color"
                    >
                        <Highlighter size={15} />
                    </button>
                    {toolbarHlOpen && (
                        <div className="tiptap-color-dropdown tiptap-color-dropdown--toolbar">
                            <ColorSwatches onSelect={applyHighlight} colors={HIGHLIGHT_COLORS} />
                        </div>
                    )}
                </div>

                <button
                    className={`tiptap-toolbar__btn ${editor.isActive('superscript') ? 'tiptap-toolbar__btn--active' : ''}`}
                    onClick={() => editor.chain().focus().toggleSuperscript().run()}
                    title="Superscript"
                >
                    <SuperscriptIcon size={15} />
                </button>
                <button
                    className={`tiptap-toolbar__btn ${editor.isActive('subscript') ? 'tiptap-toolbar__btn--active' : ''}`}
                    onClick={() => editor.chain().focus().toggleSubscript().run()}
                    title="Subscript"
                >
                    <SubscriptIcon size={15} />
                </button>
            </div>

            <div className="tiptap-toolbar__divider" />

            <div className="tiptap-toolbar__group">
                <button
                    className={`tiptap-toolbar__btn ${editor.isActive('heading', { level: 1 }) ? 'tiptap-toolbar__btn--active' : ''}`}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    title="Heading 1"
                >
                    <Heading1 size={15} />
                </button>
                <button
                    className={`tiptap-toolbar__btn ${editor.isActive('heading', { level: 2 }) ? 'tiptap-toolbar__btn--active' : ''}`}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    title="Heading 2"
                >
                    <Heading2 size={15} />
                </button>
                <button
                    className={`tiptap-toolbar__btn ${editor.isActive('bulletList') ? 'tiptap-toolbar__btn--active' : ''}`}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    title="Bullet List"
                >
                    <List size={15} />
                </button>
                <button
                    className={`tiptap-toolbar__btn ${editor.isActive('orderedList') ? 'tiptap-toolbar__btn--active' : ''}`}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    title="Numbered List"
                >
                    <ListOrdered size={15} />
                </button>
                <button
                    className={`tiptap-toolbar__btn ${editor.isActive('taskList') ? 'tiptap-toolbar__btn--active' : ''}`}
                    onClick={() => editor.chain().focus().toggleTaskList().run()}
                    title="Task List"
                >
                    <CheckSquare size={15} />
                </button>
                <button
                    className={`tiptap-toolbar__btn ${editor.isActive('blockquote') ? 'tiptap-toolbar__btn--active' : ''}`}
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    title="Quote"
                >
                    <Quote size={15} />
                </button>
            </div>

            <div className="tiptap-toolbar__divider" />

            {/* Color picker */}
            <div className="tiptap-toolbar__group" ref={toolbarColorRef} style={{ position: 'relative' }}>
                <button
                    className="tiptap-toolbar__btn"
                    onClick={() => setToolbarColorOpen(o => !o)}
                    title="Text Color"
                >
                    <Palette size={15} />
                </button>
                {toolbarColorOpen && (
                    <div className="tiptap-color-dropdown tiptap-color-dropdown--toolbar">
                        <ColorSwatches onSelect={applyColor} />
                    </div>
                )}
                <button
                    className="tiptap-toolbar__btn"
                    onClick={() => {
                        const url = window.prompt('Enter image URL:');
                        if (url) editor.chain().focus().setImage({ src: url }).run();
                    }}
                    title="Insert Image"
                >
                    <ImageIcon size={15} />
                </button>
            </div>

            <div className="tiptap-toolbar__divider" />

            <div className="tiptap-toolbar__group">
                <button
                    className="tiptap-toolbar__btn"
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                    title="Undo"
                >
                    <Undo2 size={15} />
                </button>
                <button
                    className="tiptap-toolbar__btn"
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                    title="Redo"
                >
                    <Redo2 size={15} />
                </button>
            </div>
        </div>
    );
}
